import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollAnimations';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingDown, TrendingUp, Calculator, Download, ArrowRight, CheckCircle2 } from 'lucide-react';

const API_BASE_URL = '/api';

export default function ROICalculator() {
  const { ref, isVisible } = useScrollReveal();
  const [employees, setEmployees] = useState('50');
  const [replacementFreq, setReplacementFreq] = useState('2');
  const [currentCost, setCurrentCost] = useState('8000');
  const [showResults, setShowResults] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const [submitted, setSubmitted] = useState(false);

  const numEmployees = parseInt(employees) || 0;
  const freq = parseInt(replacementFreq) || 1;
  const cost = parseInt(currentCost) || 0;

  const annualSpend = numEmployees * cost * freq;
  const bulkDiscount = 0.25;
  const bulkAnnualSpend = annualSpend * (1 - bulkDiscount);
  const annualSavings = annualSpend - bulkAnnualSpend;
  const threeYearSavings = annualSavings * 3;
  const qualityBonus = annualSpend * 0.15;

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setShowResults(true);
  };

  const handleGetReport = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/lead-magnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, magnetType: 'roi-calculator' }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSubmitted(true);
        setTimeout(() => setShowForm(false), 2000);
      }
    } catch {
      // silent fail
    }
  };

  return (
    <section id="roi-calculator" className="py-24 bg-gradient-to-br from-primary/5 via-background to-primary/5">
      <div ref={ref} className="container mx-auto px-4">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Free Tool</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">
            Corporate Branding ROI Calculator
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Discover how much your business could save by switching to bulk, quality-branded uniforms and merchandise.
          </p>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Card className="border-2 border-primary/20 shadow-xl overflow-hidden">
            <div className="bg-primary/5 p-6 border-b border-primary/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold font-display text-foreground">Calculate Your Savings</h3>
                  <p className="text-sm text-muted-foreground">Enter your current uniform/merchandise details below</p>
                </div>
              </div>
            </div>

            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleCalculate} className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="space-y-2">
                  <Label htmlFor="employees">Number of Employees / Students</Label>
                  <Input
                    id="employees"
                    type="number"
                    min="1"
                    value={employees}
                    onChange={(e) => { setEmployees(e.target.value); setShowResults(false); }}
                    className="bg-muted/50 text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="freq">Replacement Frequency (per year)</Label>
                  <Input
                    id="freq"
                    type="number"
                    min="1"
                    max="4"
                    value={replacementFreq}
                    onChange={(e) => { setReplacementFreq(e.target.value); setShowResults(false); }}
                    className="bg-muted/50 text-lg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cost">Avg. Cost Per Item (₦)</Label>
                  <Input
                    id="cost"
                    type="number"
                    min="100"
                    value={currentCost}
                    onChange={(e) => { setCurrentCost(e.target.value); setShowResults(false); }}
                    className="bg-muted/50 text-lg"
                  />
                </div>
              </form>

              <Button
                onClick={handleCalculate}
                className="w-full bg-primary hover:bg-primary-glow text-primary-foreground text-lg py-6 mb-8"
              >
                <Calculator className="w-5 h-5 mr-2" /> Calculate My ROI
              </Button>

              {showResults && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {/* Current Spend */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">Current Annual Spend</span>
                      </div>
                      <p className="text-2xl font-bold text-red-700 dark:text-red-300">₦{annualSpend.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {numEmployees} people × ₦{cost.toLocaleString()} × {freq}x/year
                      </p>
                    </div>

                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-green-500" />
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">Bulk Order Savings (25%)</span>
                      </div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-300">₦{annualSavings.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">Saved every year with bulk ordering</p>
                    </div>
                  </div>

                  {/* 3-Year Projection */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="font-bold font-display text-foreground mb-4">3-Year Projection</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <p className="text-3xl font-bold text-primary">₦{threeYearSavings.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-1">Total Savings</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <p className="text-3xl font-bold text-primary">₦{qualityBonus.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-1">Quality Bonus (reduced replacements)</p>
                      </div>
                      <div className="text-center p-4 bg-muted/50 rounded-lg">
                        <p className="text-3xl font-bold text-primary">₦{(threeYearSavings + qualityBonus).toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground mt-1">Total 3-Year Value</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  {!submitted ? !showForm ? (
                    <div className="text-center space-y-4">
                      <p className="text-muted-foreground">
                        Want a detailed, customized ROI report for your organization?
                      </p>
                      <Button
                        onClick={() => setShowForm(true)}
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                      >
                        Get My Custom Report <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleGetReport} className="bg-muted/30 rounded-xl p-6 space-y-4">
                      <h4 className="font-bold font-display text-foreground">Get Your Custom ROI Report</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="roi-name">Full Name *</Label>
                          <Input
                            id="roi-name"
                            placeholder="John Doe"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="roi-email">Email *</Label>
                          <Input
                            id="roi-email"
                            type="email"
                            placeholder="john@company.com"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="roi-phone">Phone</Label>
                          <Input
                            id="roi-phone"
                            placeholder="+234 800 000 0000"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="roi-company">Company / School</Label>
                          <Input
                            id="roi-company"
                            placeholder="Your organization name"
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            className="bg-background"
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full bg-primary hover:bg-primary-glow">
                        <Download className="w-4 h-4 mr-2" /> Send Me the Report
                      </Button>
                    </form>
                  ) : (
                    <div className="text-center py-4 bg-green-50 dark:bg-green-950/30 rounded-xl">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="font-semibold text-green-700 dark:text-green-300">Report requested! Check your email shortly.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
